/**
 * DidYouBookPrompt — "did you book it?" on the way back from the handoff (B5).
 *
 * Wade, 2026-08-06: *"I didn't tell it I booked so it shouldn't know."* This is
 * how it comes to know. B4's write path has existed and been verified since
 * #185; nothing asked the question. This asks it.
 *
 * THREE RULES, and each one is load-bearing:
 *
 *  1. It never assumes. Opening aa.com is a tap on a link — the traveller may
 *     have closed the tab, or the fare may have moved. This component only ever
 *     ASKS; the recording happens through BookTripSheet's existing review +
 *     confirm gate (B5), which is also where the price basis and the record
 *     locator get captured properly.
 *  2. "Not yet" is a real answer and it is remembered server-side. Not a
 *     snooze, not a dismissal — the unprompted question stops until the
 *     traveller goes back to the airline again. A watcher that asks twice gets
 *     muted, and a muted watcher is worth nothing on the day it matters.
 *  3. It is dismissible. The ✕ hides it for this visit without spending the
 *     "Not yet" answer, for the traveller who is mid-something and will deal
 *     with it later.
 *
 * Rendered in two places, deliberately: INLINE in the booking sheet (the tab is
 * still open behind the new one, so this is the first thing they see on
 * returning) and as a BANNER on the trip page (so the question survives a
 * reload, a closed sheet, or coming back on another device).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, X, Plane } from "lucide-react";
import { bookingApi } from "@/lib/api";
import type { PlanLeg } from "@/lib/types";

interface Props {
  tripId: string;
  /** The legs we handed off and have no booking for. Never empty when shown. */
  legs: PlanLeg[];
  /** Open the record flow (BookTripSheet's "I already booked" mode). */
  onRecord: (legs: PlanLeg[]) => void;
  /** Fired after "Not yet" is stored, so a host sheet can close itself. */
  onAnswered?: () => void;
  variant?: "banner" | "inline";
}

/** "Dallas → Vancouver" — cities when we have them, codes when we don't (R7). */
function legName(leg: PlanLeg): string {
  return (leg.originCity || leg.origin) + " → " + (leg.destinationCity || leg.destination);
}

export default function DidYouBookPrompt({
  tripId,
  legs,
  onRecord,
  onAnswered,
  variant = "banner",
}: Props) {
  // ✕ = "not now", this visit only. Distinct from "Not yet", which is an answer
  // we persist. Conflating the two would either nag someone who answered, or
  // silently answer for someone who just closed a panel.
  const [hidden, setHidden] = useState(false);
  const qc = useQueryClient();

  const notYet = useMutation({
    mutationFn: async () => {
      const [first, ...rest] = legs;
      await bookingApi.markHandoff(
        first.routeId,
        "not_yet",
        rest.map((l) => l.routeId),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tripPlan", tripId] });
      onAnswered?.();
    },
    // A failed write must not leave a prompt claiming to have remembered
    // something it didn't. We simply leave it on screen; the next answer retries.
    onError: () => setHidden(true),
  });

  if (hidden || legs.length === 0) return null;

  const single = legs.length === 1;
  const names = legs.map(legName);

  return (
    <div
      role="status"
      className={
        "relative rounded-lg border-2 border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/[0.04] p-3 " +
        (variant === "banner" ? "shadow-paper" : "")
      }
    >
      <button
        type="button"
        onClick={() => setHidden(true)}
        aria-label="Not now — ask me again later"
        className="absolute right-2 top-2 rounded p-1 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>

      <div className="flex items-start gap-2 pr-6">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--primary))]/[0.12] text-[hsl(var(--accent-foreground))]">
          <Plane className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">
            {single ? "Did you book it?" : "Did you book these?"}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {/* Say plainly that we don't know. We handed them off to another
                site; we cannot see what happened there and must not pretend. */}
            We sent you to the airline&apos;s own site, so we can&apos;t see how it went
            {names.length > 0 && <> — {names.join(" and ")}</>}. Tell us and your trip
            will show what you actually paid instead of a live quote.
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onRecord(legs)}
              className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--foreground))] px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
            >
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Yes — record what I paid
            </button>
            <button
              type="button"
              disabled={notYet.isPending}
              onClick={() => notYet.mutate()}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-60"
            >
              {notYet.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              Not yet
            </button>
          </div>

          {/* The "Not yet" contract, stated so it can be trusted. */}
          <div className="mt-1.5 text-[11px] text-muted-foreground">
            &ldquo;Not yet&rdquo; is remembered — we won&apos;t keep asking. You can record a
            booking any time from <span className="font-medium">Already booked?</span>
          </div>
        </div>
      </div>
    </div>
  );
}
